import React, { useEffect, useState, useMemo } from 'react';
import Layouts from '../../layouts/Layouts';
import { http, getAuthorizationHeader } from '../../config/http';
import { toast } from 'react-toastify';
import moment from 'moment';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; 
import 'react-date-range/dist/theme/default.css';
import {
    IconCalendarTime,
    IconReload,
    IconCash,
    IconQrcode,
    IconBuildingBank,
    IconReceipt2,
    IconCreditCard,
    IconArrowsSplit,
    IconSearch,
    IconFileSpreadsheet,
    IconChevronLeft,
    IconChevronRight,
    IconFilter,
    IconDiamond,
    IconScale
} from '@tabler/icons-react';
import { DinamicTitle } from '../../utils/DinamicTitle';
import * as XLSX from 'xlsx';

function numberWithCommas(x) {
    if (x === null || x === undefined || x === '') return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function checkBerat(number) {
    if (number === null || number === undefined || number === '') return '0';
    const beratConfirm = parseFloat(number);
    return beratConfirm % 1 === 0
        ? beratConfirm.toString()
        : beratConfirm.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
}

export default function LaporanPembayaran() {
    DinamicTitle('Laporan Pembayaran || Komaladewi');

    const role = localStorage.getItem('role');

    // Tabs: 'rekap' | 'split'
    const [activeTab, setActiveTab] = useState('rekap');

    const [summary, setSummary] = useState(null);
    const [splitData, setSplitData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filters
    const [cabangFilter, setCabangFilter] = useState('');
    const [cabangList, setCabangList] = useState([]);

    // Date filter
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());

    // Split Tab specific filters & pagination
    const [searchKeyword, setSearchKeyword] = useState('');
    const [metodeFilter, setMetodeFilter] = useState('all'); // all, cash, qris, transfer, edc
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    const selectionRange = {
        startDate: startDate || new Date(),
        endDate: endDate || new Date(),
        key: 'selection',
    };

    const handleSelectDate = (date) => {
        setStartDate(date.selection.startDate);
        setEndDate(date.selection.endDate);
        setCurrentPage(1);
    };

    const handleResetDate = () => {
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
    };

    const awal = startDate ? moment(startDate).format('DD-MM-YYYY') : '';
    const akhir = endDate ? moment(endDate).format('DD-MM-YYYY') : '';

    useEffect(() => {
        if (role === 'owner' || role === 'admin') {
            http.get(`/cabang`, {
                headers: { Authorization: getAuthorizationHeader() },
            }).then(res => {
                const stores = res.data.data.filter(c => c.is_toko === 1 || c.is_toko === '1' || c.is_toko === true);
                setCabangList(stores);
            }).catch(() => {});
        }
    }, [role]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cabangFilter, startDate, endDate]);

    const fetchData = async () => {
        setLoading(true);
        const tgl_awal = startDate ? moment(startDate).format('YYYY-MM-DD') : '';
        const tgl_akhir = endDate ? moment(endDate).format('YYYY-MM-DD') : '';

        let url = `/laporan-pembayaran?tgl_awal=${tgl_awal}&tgl_akhir=${tgl_akhir}`;
        if (cabangFilter) {
            url += `&cabang=${cabangFilter}`;
        }

        try {
            const res = await http.get(url, { headers: { Authorization: getAuthorizationHeader() } });
            setSummary(res.data.data);

            // Ambil split penjualan dari backend
            if (Array.isArray(res.data.split_penjualan)) {
                setSplitData(res.data.split_penjualan);
            } else if (Array.isArray(res.data.detail)) {
                // Fallback jika API mengembalikan data detail
                const grouped = {};
                res.data.detail.forEach(p => {
                    const pId = p.penjualan_id || p.penjualan?.id;
                    if (!pId) return;
                    if (!grouped[pId]) {
                        grouped[pId] = {
                            id: pId,
                            kode_trx: p.penjualan?.kode_trx || `FKDJ-${pId}`,
                            created_at: p.penjualan?.created_at || p.created_at,
                            kasir: p.penjualan?.user?.name || '-',
                            cabang: p.penjualan?.user?.cabang?.nama_cabang || '-',
                            item: {
                                id: p.penjualan?.item?.id,
                                kode_item: p.penjualan?.item?.kode_item || '-',
                                nama_barang: p.penjualan?.item?.jenis_kategori?.nama_jenis || p.penjualan?.item?.nama_barang || '-',
                                karat: p.penjualan?.item?.karat || 0,
                                berat: p.penjualan?.item?.berat || 0,
                            },
                            harga_jual: Number(p.penjualan?.harga_jual) || 0,
                            total_harga: Number(p.penjualan?.total_harga || p.penjualan?.harga_jual) || 0,
                            payments: [],
                            cash: 0,
                            qris: 0,
                            transfer: 0,
                            transfer_bank: '',
                            edc: 0,
                            jumlah_metode: 0
                        };
                    }
                    grouped[pId].payments.push(p);
                    const m = (p.metode_pembayaran || '').toLowerCase();
                    const nom = Number(p.nominal) || 0;
                    if (m === 'cash') grouped[pId].cash += nom;
                    else if (m === 'qris') grouped[pId].qris += nom;
                    else if (m === 'edc') grouped[pId].edc += nom;
                    else if (m === 'transfer') {
                        grouped[pId].transfer += nom;
                        if (p.nama_bank) {
                            grouped[pId].transfer_bank = grouped[pId].transfer_bank ? `${grouped[pId].transfer_bank}, ${p.nama_bank}` : p.nama_bank;
                        }
                    }
                });

                const parsedSplits = Object.values(grouped).filter(item => item.payments.length > 1);
                parsedSplits.forEach(item => { item.jumlah_metode = item.payments.length; });
                setSplitData(parsedSplits);
            } else {
                setSplitData([]);
            }
        } catch (error) {
            toast.error("Gagal mengambil data pembayaran.");
        }
        setLoading(false);
    };

    // Filtered Split Data
    const filteredSplitData = useMemo(() => {
        return splitData.filter(trx => {
            if (searchKeyword.trim() !== '') {
                const q = searchKeyword.toLowerCase();
                const kodeTrxMatch = trx.kode_trx?.toLowerCase().includes(q);
                const kodeItemMatch = trx.item?.kode_item?.toLowerCase().includes(q);
                const namaBarangMatch = trx.item?.nama_barang?.toLowerCase().includes(q);
                const kasirMatch = trx.kasir?.toLowerCase().includes(q);
                const cabangMatch = trx.cabang?.toLowerCase().includes(q);

                if (!kodeTrxMatch && !kodeItemMatch && !namaBarangMatch && !kasirMatch && !cabangMatch) {
                    return false;
                }
            }

            if (metodeFilter === 'cash' && (trx.cash || 0) <= 0) return false;
            if (metodeFilter === 'qris' && (trx.qris || 0) <= 0) return false;
            if (metodeFilter === 'transfer' && (trx.transfer || 0) <= 0) return false;
            if (metodeFilter === 'edc' && (trx.edc || 0) <= 0) return false;

            return true;
        });
    }, [splitData, searchKeyword, metodeFilter]);

    // Totals for Split Data
    const splitStats = useMemo(() => {
        let totalNilai = 0;
        let totalCash = 0;
        let totalQris = 0;
        let totalTransfer = 0;
        let totalEdc = 0;

        filteredSplitData.forEach(trx => {
            totalNilai += Number(trx.total_harga) || Number(trx.harga_jual) || 0;
            totalCash += Number(trx.cash) || 0;
            totalQris += Number(trx.qris) || 0;
            totalTransfer += Number(trx.transfer) || 0;
            totalEdc += Number(trx.edc) || 0;
        });

        return {
            count: filteredSplitData.length,
            totalNilai,
            totalCash,
            totalQris,
            totalTransfer,
            totalEdc
        };
    }, [filteredSplitData]);

    // Pagination
    const totalPages = Math.ceil(filteredSplitData.length / pageSize) || 1;
    const paginatedSplitData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredSplitData.slice(start, start + pageSize);
    }, [filteredSplitData, currentPage, pageSize]);

    // Export to Excel
    const handleExportExcel = () => {
        if (!filteredSplitData || filteredSplitData.length === 0) {
            toast.warn("Tidak ada data split pembayaran untuk diexport.");
            return;
        }

        const rows = filteredSplitData.map((trx, idx) => ({
            'No': idx + 1,
            'No Faktur': trx.kode_trx || '-',
            'Waktu': trx.created_at || '-',
            'Cabang': trx.cabang || '-',
            'Kasir': trx.kasir || '-',
            'Kode Barang': trx.item?.kode_item || '-',
            'Nama Barang': trx.item?.nama_barang || '-',
            'Karat': `${trx.item?.karat || 0} K`,
            'Berat (gr)': parseFloat(trx.item?.berat || 0),
            'Total Harga (Rp)': Number(trx.total_harga || trx.harga_jual || 0),
            'Cash (Rp)': Number(trx.cash || 0),
            'QRIS (Rp)': Number(trx.qris || 0),
            'Transfer (Rp)': Number(trx.transfer || 0),
            'Bank Transfer': trx.transfer_bank || '-',
            'EDC (Rp)': Number(trx.edc || 0),
            'Rincian Pembayaran': (trx.payments || [])
                .map(p => `${(p.metode_pembayaran || '').toUpperCase()}${p.nama_bank ? ' (' + p.nama_bank + ')' : ''}: Rp ${numberWithCommas(p.nominal)}`)
                .join(' | ')
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Split Pembayaran');
        const filename = `Laporan_Split_Pembayaran_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
        XLSX.writeFile(workbook, filename);
        toast.success(`Berhasil export data ke ${filename}`);
    };

    return (
        <Layouts>
            <div className="bg-slate-50 dark:bg-slate-900 min-h-screen pb-12">
                {/* Header Section */}
                <div className="bg-white dark:bg-slate-800 p-5 md:p-6 border-b border-slate-200 dark:border-slate-700 shadow-sm">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                    <IconReceipt2 size={24} />
                                </span>
                                <h1 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-white">
                                    Laporan Metode Pembayaran
                                </h1>
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm mt-1 ml-10">
                                Rekapitulasi pembayaran & data transaksi khusus multi-metode (split bayar)
                            </p>
                        </div>

                        {/* Global Filters */}
                        <div className="flex flex-wrap items-center gap-2.5">
                            {(role === 'admin' || role === 'owner') && (
                                <select
                                    className="select select-bordered select-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white rounded-xl text-xs h-[38px]"
                                    value={cabangFilter}
                                    onChange={(e) => {
                                        setCabangFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="">Semua Cabang</option>
                                    {cabangList.map(cab => (
                                        <option key={cab.id} value={cab.id}>{cab.nama_cabang}</option>
                                    ))}
                                </select>
                            )}

                            {/* Date Range Dropdown */}
                            <div className="dropdown dropdown-bottom">
                                <div
                                    tabIndex={0}
                                    className="px-3.5 py-2 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 cursor-pointer hover:border-amber-400 transition-all duration-200 flex items-center gap-2 text-xs h-[38px]"
                                >
                                    <IconCalendarTime size={16} className="text-amber-500" />
                                    <span className="font-semibold">
                                        {startDate && endDate ? (awal === akhir ? awal : `${awal} - ${akhir}`) : 'Semua Tanggal'}
                                    </span>
                                </div>
                                <div tabIndex={0} className="dropdown-content menu p-2 shadow-2xl bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 z-50 mt-1 right-0 w-max">
                                    <DateRange ranges={[selectionRange]} onChange={handleSelectDate} />
                                    {startDate && (
                                        <div className="p-2 border-t border-slate-100 dark:border-slate-700 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={handleResetDate}
                                                className="text-xs text-rose-500 hover:underline font-semibold"
                                            >
                                                Reset Tanggal (Semua)
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={fetchData}
                                className="btn btn-sm bg-slate-800 hover:bg-slate-900 text-white border-none rounded-xl h-[38px] px-3.5 gap-1.5 dark:bg-slate-700 dark:hover:bg-slate-600"
                                disabled={loading}
                                title="Muat Ulang Data"
                            >
                                <IconReload size={16} className={loading ? 'animate-spin text-amber-400' : 'text-amber-400'} />
                                <span className="text-xs">Refresh</span>
                            </button>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex items-center gap-2 mt-6 border-b border-slate-200 dark:border-slate-700 pt-2">
                        <button
                            type="button"
                            onClick={() => setActiveTab('rekap')}
                            className={`flex items-center gap-2 pb-3 px-4 font-bold text-sm transition-all border-b-2 relative ${
                                activeTab === 'rekap'
                                    ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                            }`}
                        >
                            <span>Rekapitulasi</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveTab('split')}
                            className={`flex items-center gap-2 pb-3 px-4 font-bold text-sm transition-all border-b-2 relative ${
                                activeTab === 'split'
                                    ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                            }`}
                        >
                            <span>Split</span>
                        </button>
                    </div>
                </div>

                {/* Content Body */}
                <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center p-20 gap-3">
                            <div className="w-12 h-12 rounded-full animate-spin border-4 border-dashed border-amber-500 border-t-transparent" />
                            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                                Memuat data laporan pembayaran...
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* TAB 1: REKAPITULASI METODE PEMBAYARAN */}
                            {activeTab === 'rekap' && (
                                summary ? (
                                    <div className="space-y-6">
                                        {/* Summary Cards */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                            {/* Cash */}
                                            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow transition flex items-center gap-4">
                                                <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center shrink-0">
                                                    <IconCash size={26} />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Cash</p>
                                                    <p className="text-xl font-extrabold text-slate-800 dark:text-white truncate mt-0.5">
                                                        Rp {numberWithCommas(summary.cash)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* QRIS */}
                                            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow transition flex items-center gap-4">
                                                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center shrink-0">
                                                    <IconQrcode size={26} />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total QRIS</p>
                                                    <p className="text-xl font-extrabold text-slate-800 dark:text-white truncate mt-0.5">
                                                        Rp {numberWithCommas(summary.qris)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Transfer Bank */}
                                            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow transition flex items-center gap-4">
                                                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-2xl flex items-center justify-center shrink-0">
                                                    <IconBuildingBank size={26} />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Transfer</p>
                                                    <p className="text-xl font-extrabold text-slate-800 dark:text-white truncate mt-0.5">
                                                        Rp {numberWithCommas(summary.transfer?.total || 0)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* EDC / Debit */}
                                            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow transition flex items-center gap-4">
                                                <div className="w-12 h-12 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-2xl flex items-center justify-center shrink-0">
                                                    <IconCreditCard size={26} />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total EDC</p>
                                                    <p className="text-xl font-extrabold text-slate-800 dark:text-white truncate mt-0.5">
                                                        Rp {numberWithCommas(summary.edc || 0)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Total Keseluruhan */}
                                            <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-5 rounded-2xl border border-amber-500 shadow-lg shadow-amber-500/20 text-white flex items-center gap-4">
                                                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shrink-0">
                                                    <IconReceipt2 size={26} className="text-white" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold uppercase tracking-wider text-amber-100">Total Keseluruhan</p>
                                                    <p className="text-xl font-black truncate mt-0.5">
                                                        Rp {numberWithCommas(summary.total_keseluruhan)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Detail Transfer Bank */}
                                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/80 flex items-center justify-between">
                                                <div className="flex items-center gap-2.5">
                                                    <IconBuildingBank className="text-purple-600 dark:text-purple-400" size={20} />
                                                    <h3 className="font-bold text-slate-800 dark:text-white text-base">
                                                        Rincian Transfer Bank
                                                    </h3>
                                                </div>
                                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                                    {Object.keys(summary.transfer?.banks || {}).length} Bank Terdeteksi
                                                </span>
                                            </div>
                                            <div className="p-0">
                                                {summary.transfer?.banks && Object.keys(summary.transfer.banks).length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="table w-full">
                                                            <thead>
                                                                <tr className="bg-slate-50/50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-xs uppercase">
                                                                    <th className="w-16 text-center py-3.5">No</th>
                                                                    <th className="py-3.5">Nama Bank</th>
                                                                    <th className="text-right py-3.5 pr-8">Total Nominal</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {Object.entries(summary.transfer.banks).map(([bank, nominal], index) => (
                                                                    <tr key={bank} className="border-b border-slate-100 dark:border-slate-700/60 hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition">
                                                                        <td className="text-center text-slate-500 py-3">{index + 1}</td>
                                                                        <td className="font-semibold text-slate-800 dark:text-slate-100 py-3">
                                                                            <span className="px-2.5 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-bold text-xs border border-purple-200 dark:border-purple-800/40">
                                                                                {bank}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-right font-extrabold text-purple-600 dark:text-purple-400 py-3 pr-8 font-mono">
                                                                            Rp {numberWithCommas(nominal)}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                <tr className="bg-slate-50 dark:bg-slate-900/80 font-bold border-t-2 border-slate-200 dark:border-slate-700">
                                                                    <td colSpan={2} className="text-right text-slate-700 dark:text-slate-300 py-3.5">
                                                                        Total Transfer Keseluruhan :
                                                                    </td>
                                                                    <td className="text-right text-slate-900 dark:text-white py-3.5 pr-8 font-mono text-base font-extrabold">
                                                                        Rp {numberWithCommas(summary.transfer.total)}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="p-12 text-center text-slate-500 dark:text-slate-400 text-sm">
                                                        Tidak ada data transaksi transfer bank pada periode ini.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-16 text-center text-slate-500">
                                        Pilih rentang tanggal dan cabang untuk menampilkan rekapitulasi.
                                    </div>
                                )
                            )}

                            {/* TAB 2: TRANSAKSI SPLIT PEMBAYARAN */}
                            {activeTab === 'split' && (
                                <div className="space-y-5">
                                    {/* Mini Stats for Split Payments */}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                                        {/* Total Transaksi Split */}
                                        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Trx Split</p>
                                            <p className="text-lg font-black text-amber-600 dark:text-amber-400 mt-0.5">
                                                {splitStats.count} <span className="text-xs font-normal text-slate-400">Trx</span>
                                            </p>
                                        </div>

                                        {/* Total Nilai Split */}
                                        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Total Penjualan</p>
                                            <p className="text-base font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono truncate">
                                                Rp {numberWithCommas(splitStats.totalNilai)}
                                            </p>
                                        </div>

                                        {/* Split Cash */}
                                        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Split Cash</p>
                                            <p className="text-base font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 font-mono truncate">
                                                Rp {numberWithCommas(splitStats.totalCash)}
                                            </p>
                                        </div>

                                        {/* Split QRIS */}
                                        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                            <p className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Split QRIS</p>
                                            <p className="text-base font-extrabold text-blue-600 dark:text-blue-400 mt-0.5 font-mono truncate">
                                                Rp {numberWithCommas(splitStats.totalQris)}
                                            </p>
                                        </div>

                                        {/* Split Transfer */}
                                        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                            <p className="text-[11px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">Split Transfer</p>
                                            <p className="text-base font-extrabold text-purple-600 dark:text-purple-400 mt-0.5 font-mono truncate">
                                                Rp {numberWithCommas(splitStats.totalTransfer)}
                                            </p>
                                        </div>

                                        {/* Split EDC */}
                                        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                            <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">Split EDC</p>
                                            <p className="text-base font-extrabold text-cyan-600 dark:text-cyan-400 mt-0.5 font-mono truncate">
                                                Rp {numberWithCommas(splitStats.totalEdc)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Action & Filter Bar */}
                                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
                                        <div className="flex flex-1 flex-col sm:flex-row items-center gap-2.5 w-full">
                                            {/* Search */}
                                            <div className="relative w-full sm:max-w-xs">
                                                <input
                                                    type="text"
                                                    placeholder="Cari kode barang, nama, kasir..."
                                                    value={searchKeyword}
                                                    onChange={(e) => {
                                                        setSearchKeyword(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    className="w-full pl-9 pr-3.5 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                                                />
                                                <IconSearch size={16} className="absolute left-3 top-2.5 text-slate-400" />
                                            </div>

                                            {/* Filter Metode */}
                                            <div className="flex items-center gap-1.5 w-full sm:w-auto">
                                                <IconFilter size={16} className="text-slate-400 shrink-0" />
                                                <select
                                                    value={metodeFilter}
                                                    onChange={(e) => {
                                                        setMetodeFilter(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    className="select select-bordered select-sm text-xs rounded-xl dark:bg-slate-900 dark:border-slate-700 dark:text-white w-full sm:w-auto h-[36px]"
                                                >
                                                    <option value="all">Semua Metode Split</option>
                                                    <option value="cash">Mengandung CASH</option>
                                                    <option value="qris">Mengandung QRIS</option>
                                                    <option value="transfer">Mengandung Transfer</option>
                                                    <option value="edc">Mengandung EDC</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                                            <button
                                                type="button"
                                                onClick={handleExportExcel}
                                                className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white border-none rounded-xl gap-1.5 px-3.5 text-xs h-[36px] shadow-sm"
                                            >
                                                <IconFileSpreadsheet size={16} />
                                                <span>Export Excel</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Data Table */}
                                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                        <div className="overflow-x-auto scrollbar-thin">
                                            <table className="min-w-full text-left text-xs">
                                                <thead>
                                                    <tr className="bg-gradient-to-r from-amber-50 via-amber-100/60 to-amber-50 dark:from-amber-950/30 dark:via-amber-900/20 dark:to-amber-950/30 border-b border-amber-200/60 dark:border-slate-700 text-slate-700 dark:text-slate-300 uppercase tracking-wider font-bold">
                                                        <th className="py-3 px-3 text-center w-12">No</th>
                                                        <th className="py-3 px-3.5">Waktu / Tanggal</th>
                                                        <th className="py-3 px-3.5">Kasir & Cabang</th>
                                                        <th className="py-3 px-3.5">Kode Barang</th>
                                                        <th className="py-3 px-3.5">Nama Barang</th>
                                                        <th className="py-3 px-3 text-center">Karat</th>
                                                        <th className="py-3 px-3 text-right">Berat</th>
                                                        <th className="py-3 px-3.5 text-right font-extrabold text-amber-800 dark:text-amber-400">Total Harga</th>
                                                        <th className="py-3 px-3.5 text-right text-emerald-700 dark:text-emerald-400">CASH</th>
                                                        <th className="py-3 px-3.5 text-right text-blue-700 dark:text-blue-400">QRIS</th>
                                                        <th className="py-3 px-3.5 text-right text-purple-700 dark:text-purple-400">TRANSFER</th>
                                                        <th className="py-3 px-3.5 text-right text-cyan-700 dark:text-cyan-400">EDC</th>
                                                        <th className="py-3 px-3.5 text-center">Rincian Split</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                                                    {paginatedSplitData.length > 0 ? (
                                                        paginatedSplitData.map((trx, index) => {
                                                            const rowNo = (currentPage - 1) * pageSize + index + 1;
                                                            return (
                                                                <tr
                                                                    key={trx.id || index}
                                                                    className="hover:bg-amber-50/50 dark:hover:bg-slate-700/40 transition-colors"
                                                                >
                                                                    {/* No */}
                                                                    <td className="py-3 px-3 text-center text-slate-400 font-medium">
                                                                        {rowNo}
                                                                    </td>

                                                                    {/* Waktu & Tanggal */}
                                                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                                                        <div className="font-semibold text-slate-800 dark:text-slate-200">
                                                                            {trx.created_at || '-'}
                                                                        </div>
                                                                        <div className="text-[10px] text-slate-400 font-mono">
                                                                            {trx.kode_trx}
                                                                        </div>
                                                                    </td>

                                                                    {/* Kasir & Cabang */}
                                                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                                                        <div className="font-semibold text-slate-800 dark:text-slate-200">
                                                                            {trx.kasir || '-'}
                                                                        </div>
                                                                        <div className="text-[10px] text-slate-500 dark:text-slate-400">
                                                                            {trx.cabang || '-'}
                                                                        </div>
                                                                    </td>

                                                                    {/* Kode Barang */}
                                                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                                                        <span className="font-bold font-mono text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 rounded border border-amber-200/60 dark:border-amber-900/40">
                                                                            {trx.item?.kode_item || '-'}
                                                                        </span>
                                                                    </td>

                                                                    {/* Nama Barang */}
                                                                    <td className="py-3 px-3.5 whitespace-nowrap font-medium text-slate-800 dark:text-slate-200">
                                                                        {trx.item?.nama_barang || '-'}
                                                                    </td>

                                                                    {/* Karat */}
                                                                    <td className="py-3 px-3 text-center whitespace-nowrap">
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold text-[11px]">
                                                                            <IconDiamond size={12} className="text-amber-500" />
                                                                            {trx.item?.karat || 0}K
                                                                        </span>
                                                                    </td>

                                                                    {/* Berat */}
                                                                    <td className="py-3 px-3 text-right whitespace-nowrap">
                                                                        <span className="inline-flex items-center gap-1 font-mono font-bold text-slate-700 dark:text-slate-300">
                                                                            <IconScale size={12} className="text-slate-400" />
                                                                            {checkBerat(trx.item?.berat)} gr
                                                                        </span>
                                                                    </td>

                                                                    {/* Total Harga */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-mono font-extrabold text-slate-900 dark:text-white bg-slate-50/50 dark:bg-slate-800/40">
                                                                        Rp {numberWithCommas(trx.total_harga || trx.harga_jual || 0)}
                                                                    </td>

                                                                    {/* CASH */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-mono font-bold">
                                                                        {trx.cash > 0 ? (
                                                                            <span className="text-emerald-600 dark:text-emerald-400">
                                                                                Rp {numberWithCommas(trx.cash)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-slate-300 dark:text-slate-600">-</span>
                                                                        )}
                                                                    </td>

                                                                    {/* QRIS */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-mono font-bold">
                                                                        {trx.qris > 0 ? (
                                                                            <span className="text-blue-600 dark:text-blue-400">
                                                                                Rp {numberWithCommas(trx.qris)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-slate-300 dark:text-slate-600">-</span>
                                                                        )}
                                                                    </td>

                                                                    {/* TRANSFER */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-mono font-bold">
                                                                        {trx.transfer > 0 ? (
                                                                            <div>
                                                                                <span className="text-purple-600 dark:text-purple-400">
                                                                                    Rp {numberWithCommas(trx.transfer)}
                                                                                </span>
                                                                                {trx.transfer_bank && (
                                                                                    <div className="text-[10px] text-purple-500 font-sans font-normal">
                                                                                        ({trx.transfer_bank})
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-slate-300 dark:text-slate-600">-</span>
                                                                        )}
                                                                    </td>

                                                                    {/* EDC */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-mono font-bold">
                                                                        {trx.edc > 0 ? (
                                                                            <span className="text-cyan-600 dark:text-cyan-400">
                                                                                Rp {numberWithCommas(trx.edc)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-slate-300 dark:text-slate-600">-</span>
                                                                        )}
                                                                    </td>

                                                                    {/* Rincian Metode Split */}
                                                                    <td className="py-3 px-3.5 whitespace-nowrap text-center">
                                                                        <div className="flex flex-wrap items-center justify-center gap-1 max-w-[200px] mx-auto">
                                                                            {(trx.payments || []).map((p, pIdx) => {
                                                                                const m = (p.metode_pembayaran || '').toLowerCase();
                                                                                let color = 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
                                                                                if (m === 'cash') color = 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/50';
                                                                                if (m === 'qris') color = 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200 dark:border-blue-800/50';
                                                                                if (m === 'transfer') color = 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200 dark:border-purple-800/50';
                                                                                if (m === 'edc') color = 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800/50';

                                                                                return (
                                                                                    <span
                                                                                        key={pIdx}
                                                                                        className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${color}`}
                                                                                        title={`Rp ${numberWithCommas(p.nominal)}`}
                                                                                    >
                                                                                        {m.toUpperCase()}{p.nama_bank ? ` (${p.nama_bank})` : ''}
                                                                                    </span>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={13} className="py-14 text-center text-slate-400">
                                                                <div className="flex flex-col items-center justify-center gap-2">
                                                                    <IconArrowsSplit size={36} className="text-slate-300 dark:text-slate-600" />
                                                                    <p className="font-semibold text-slate-600 dark:text-slate-300">
                                                                        Tidak ada transaksi dengan split pembayaran.
                                                                    </p>
                                                                    <p className="text-xs text-slate-400">
                                                                        Coba ganti filter cabang, rentang tanggal, atau kata kunci pencarian.
                                                                    </p>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>

                                                {/* Table Footer - Total Accumulation */}
                                                {paginatedSplitData.length > 0 && (
                                                    <tfoot>
                                                        <tr className="bg-slate-100/80 dark:bg-slate-900 border-t-2 border-slate-200 dark:border-slate-700 font-bold text-slate-800 dark:text-slate-100">
                                                            <td colSpan={7} className="py-3 px-4 text-right uppercase tracking-wider text-xs">
                                                                TOTAL ({filteredSplitData.length} Trx Terfilter) :
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right font-mono text-xs sm:text-sm text-slate-900 dark:text-white font-black bg-slate-200/40 dark:bg-slate-800/60">
                                                                Rp {numberWithCommas(splitStats.totalNilai)}
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right font-mono text-xs sm:text-sm text-emerald-600 dark:text-emerald-400 font-extrabold">
                                                                Rp {numberWithCommas(splitStats.totalCash)}
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right font-mono text-xs sm:text-sm text-blue-600 dark:text-blue-400 font-extrabold">
                                                                Rp {numberWithCommas(splitStats.totalQris)}
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right font-mono text-xs sm:text-sm text-purple-600 dark:text-purple-400 font-extrabold">
                                                                Rp {numberWithCommas(splitStats.totalTransfer)}
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right font-mono text-xs sm:text-sm text-cyan-600 dark:text-cyan-400 font-extrabold">
                                                                Rp {numberWithCommas(splitStats.totalEdc)}
                                                            </td>
                                                            <td className="py-3 px-3.5"></td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>

                                        {/* Pagination Footer */}
                                        {filteredSplitData.length > 0 && (
                                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                                    <span>Tampilkan</span>
                                                    <select
                                                        value={pageSize}
                                                        onChange={(e) => {
                                                            setPageSize(Number(e.target.value));
                                                            setCurrentPage(1);
                                                        }}
                                                        className="select select-bordered select-xs rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={15}>15</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                    </select>
                                                    <span>baris per halaman (Total {filteredSplitData.length} data)</span>
                                                </div>

                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                        disabled={currentPage <= 1}
                                                        className="btn btn-xs btn-outline dark:border-slate-600 dark:text-slate-300 disabled:opacity-30 rounded-lg px-2"
                                                    >
                                                        <IconChevronLeft size={14} />
                                                        <span>Prev</span>
                                                    </button>
                                                    <span className="text-xs px-2 font-semibold text-slate-700 dark:text-slate-300">
                                                        Hal {currentPage} dari {totalPages}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                        disabled={currentPage >= totalPages}
                                                        className="btn btn-xs btn-outline dark:border-slate-600 dark:text-slate-300 disabled:opacity-30 rounded-lg px-2"
                                                    >
                                                        <span>Next</span>
                                                        <IconChevronRight size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Layouts>
    );
}
